// Journal cover imagery helper. Every published post gets a premium
// fine-dining image, even the ones that never uploaded a hero. The
// picture is chosen deterministically from the post's tags (or a
// hash of the slug when no tag matches) so a given article always
// shows the same cover between reloads.
//
// All photos are curated luxury food photography from the Unsplash
// CDN. HTTP status was manually verified. Everything is served with
// auto format + crop so mobile clients get WebP and desktop clients
// get JPEG of the exact size they need.

const CDN = "https://images.unsplash.com";
const PARAMS = "auto=format&fit=crop&w=1600&q=85";

// Fresh mise en place, knife work, cutting board close-ups.
const IMG_YIELD = `${CDN}/photo-1544025162-d76694265947?${PARAMS}`;
// Dry-goods pantry, jars and containers on wooden shelves.
const IMG_INVENTORY = `${CDN}/photo-1533900298318-6b8da08a523e?${PARAMS}`;
// Ledger, calculator, notebook: quiet close of a working day.
const IMG_MARGIN = `${CDN}/photo-1554224155-6726b3ff858f?${PARAMS}`;
// Kitchen team in service, focused body language.
const IMG_ROSTER = `${CDN}/photo-1600891964599-f61ba0e24092?${PARAMS}`;
// Plated dish being finished: waste, comps, quality control theme.
const IMG_WASTE = `${CDN}/photo-1519996529931-28324d5a630e?${PARAMS}`;
// Wide, moody dining room lit by warm brass fixtures.
const IMG_DINING = `${CDN}/photo-1414235077428-338989a2e8c0?${PARAMS}`;
// Espresso, ledger, and hands: morning brief theme.
const IMG_REPORT = `${CDN}/photo-1495474472287-4d71bcdd2085?${PARAMS}`;
// Marble pass mise en place, used also on the Landing dual-launch.
const IMG_PREP = `${CDN}/photo-1466637574441-749b8f19452f?${PARAMS}`;

// Tag keyword to image mapping. First keyword that hits wins, so the
// order matters. Broader keywords sit at the bottom.
const TAG_MAP = [
  ["yield", IMG_YIELD],
  ["knife", IMG_YIELD],
  ["prep", IMG_PREP],
  ["waste", IMG_WASTE],
  ["comp", IMG_WASTE],
  ["plate", IMG_WASTE],
  ["margin", IMG_MARGIN],
  ["quarter", IMG_MARGIN],
  ["finance", IMG_MARGIN],
  ["cost", IMG_MARGIN],
  ["inventory", IMG_INVENTORY],
  ["invoice", IMG_INVENTORY],
  ["stock", IMG_INVENTORY],
  ["pantry", IMG_INVENTORY],
  ["automation", IMG_INVENTORY],
  ["roster", IMG_ROSTER],
  ["labor", IMG_ROSTER],
  ["labour", IMG_ROSTER],
  ["shift", IMG_ROSTER],
  ["schedule", IMG_ROSTER],
  ["reporting", IMG_REPORT],
  ["report", IMG_REPORT],
  ["audit", IMG_REPORT],
  ["dining", IMG_DINING],
  ["service", IMG_DINING],
  ["menu", IMG_DINING],
];

// Fallback cover pool: used when nothing in TAG_MAP matches. We hash
// the slug so a given post always lands on the same cover.
const FALLBACKS = [IMG_PREP, IMG_DINING, IMG_REPORT, IMG_YIELD];

function hashString(s) {
  let h = 0;
  for (let i = 0; i < s.length; i += 1) {
    h = (h * 31 + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

// Returns a cover image URL for a post. Prefers the post's own
// hero_image (resolved by the caller); falls back to a tag-matched
// image; falls back again to a slug-hashed image from the pool.
// Never returns null so the UI can render a cover unconditionally.
export function coverForPost(post) {
  const tags = (post?.tags || []).map((t) => String(t).toLowerCase());
  for (const tag of tags) {
    for (const [needle, url] of TAG_MAP) {
      if (tag.includes(needle)) return url;
    }
  }
  const seed = post?.slug || post?.id || post?.title || "journal";
  return FALLBACKS[hashString(seed) % FALLBACKS.length];
}
